<template>
    <div
        class="sbs-textarea"
        :class="textareaClasses">
        <div
            class="sbs-textarea__wrapper trs"
            :class="textareaWrapperClasses"
            @click="textareaWrapperClickHandler">
            <!-- @slot Слот перед текстовым полем -->
            <slot name="prepend"></slot>

            <div class="sbs-textarea__field-wrapper">
                <textarea
                    v-bind="inputAttrs"
                    v-model="textareaModel"
                    ref="textarea"
                    :id="textareaId"
                    class="sbs-textarea__field trs"
                    :class="textareaFieldClasses"
                    :style="textareaStyles"
                    :disabled="disabled"
                    :rows="rows"
                    v-on="textareaFieldListeners">
                </textarea>
            </div>

            <label
                v-if="label"
                class="sbs-textarea__label trs"
                :class="textareaLabelClass"
                :for="textareaId"
                :title="focused && label">
                <span class="sbs-textarea__label-text text-clamp trs">
                    {{ label }}
                </span>
            </label>

            <!--
                @slot Слот для счётчика введеных символов.
                Доступны пропсы `maxLength`, `minLength`, и `value`
            -->
            <slot
                name="counter"
                v-bind="{
                    maxLength,
                    minLength,
                    value: modelValue,
                }">
                <span
                    v-if="isCounterVisible"
                    class="sbs-textarea__counter text-clamp trs">
                    {{ counterText }}
                </span>
            </slot>

            <!-- @slot Слот после текстового поля -->
            <slot name="append"></slot>
        </div>

        <!--
            @slot Слот для сообщений об ошибках.
            Доступны пропсы `rules`, `valid`, и `value`
        -->
        <slot
            name="error"
            v-bind="{
                rules,
                valid,
                value: modelValue,
            }">
            <div
                v-if="invalid"
                class="sbs-textarea__error-message">
                <SbsIcon
                    v-bind="errorIconData"
                    class="sbs-textarea__error-icon" />
                {{ errorMessage || defaultErrorMessage }}
            </div>
        </slot>
    </div>
</template>

<script>
import _throttle from 'lodash/throttle.js';
import ResizeObserver from '../../utils/resizeObserver.js';
import dimensionsMixin from '../../mixins/dimensions.js';
import themesMixin from '../../mixins/themes.js';
import validationMixin from '../../mixins/validation.js';
import { AllColors } from '../../utils/colors.js';
import { getRandomId } from '../../utils/helpers.js';
import { AllSizes } from '../../utils/sizes.js';
import { isValueConvertedToNumber } from '../../utils/validators.js';
import { AllIconGroups } from '../icon/utils/consts.js';

const SCROLL_THROTTLE_MS = 100;

/**
 * Компонент текстового поля
 */
export default {
    name: 'SbsTextarea',
    emits: ['update:modelValue', 'focus', 'blur', 'scroll', 'scroll:reachStart', 'scroll:reachEnd', 'valid', 'invalid'],
    mixins: [dimensionsMixin, themesMixin, validationMixin],
    data() {
        return {
            focused: false,
            isScrollTop: false,
            isScrollBottom: false,
            isScrollVisible: false,
            textareaSizes: {
                height: null,
                offsetHeight: null,
            },
            resizeObserver: null,
        };
    },
    computed: {
        textareaModel: {
            get() {
                return this.modelValue;
            },
            set(value) {
                /**
                 * Эмит события `update:modelValue` с текущей введенной строкой
                 */
                this.$emit('update:modelValue', value);
            },
        },
        textareaClasses() {
            const isLabelTop = this.focused || this.modelValue;

            return [
                this.$attrs.class,
                {
                    'empty': !this.modelValue,
                    'disabled': this.disabled,
                    'focused': this.focused,
                    'invalid': this.invalid,
                    'scroll-top': this.isScrollTop,
                    'scroll-bottom': this.isScrollBottom,
                    'scroll-visible': this.isScrollVisible,
                    'full-width': this.fullWidth && !this.resizable,
                    'auto-grow': this.autoGrow && !this.resizable,
                    'resizable': this.resizable,
                    'label-top': isLabelTop,
                },
            ];
        },
        textareaStyles() {
            const { maxHeight } = this.dimensionStyles;
            const styles = {};

            if (!this.resizable) {
                styles.height = this.textareaSizes.height;
                styles.maxHeight = this.autoGrow ? maxHeight : null;
            }

            return styles;
        },
        textareaWrapperClasses() {
            return [...this.themeClasses, this.textareaWrapperClass];
        },
        textareaFieldClasses() {
            return [this.textareaFieldClass];
        },
        textareaFieldListeners() {
            return {
                ...this.inputAttrs,
                focus: this.textareaFocusHandler,
                blur: this.textareaBlurHandler,
                input: this.textareaInputHandler,
                scroll: this.textareaScrollHandler,
            };
        },
        inputAttrs() {
            const { class: _, ...attrs } = this.$attrs;

            return attrs;
        },
        textareaId() {
            return this.id || `sbs-textarea-${getRandomId()}`;
        },
        counterLimitLength() {
            return this.minLength || this.maxLength;
        },
        counterText() {
            let limitText = '';

            if (this.minLength) {
                limitText = 'Минимум';
            } else if (this.maxLength) {
                limitText = 'Максимум';
            }

            return `${limitText} ${this.counterLimitLength} символов (${this.modelValue.length} / ${this.counterLimitLength})`;
        },
        isCounterVisible() {
            let isLimitExceeded = true;

            if (this.minLength) {
                isLimitExceeded = this.modelValue.length < this.minLength;
            }

            return (
                this.counter &&
                this.counterLimitLength &&
                this.counterText &&
                isLimitExceeded
            );
        },
        errorIconData() {
            return {
                iconName: 'exclamation',
                iconGroup: AllIconGroups.SOLID,
                iconSize: AllSizes.SMALL,
                colorTheme: AllColors.WHITE,
                backgroundTheme: AllColors.GRADIENT_PINK,
            };
        },
    },
    methods: {
        textareaWrapperClickHandler() {
            this.$nextTick(() => {
                const { textarea } = this.$refs;

                if (!textarea) return;

                textarea.focus();
            });
        },
        textareaFocusHandler() {
            this.focused = true;

            /**
             * Событие `focus` срабатывает при фокусе в текстовом поле.
             * В качестве параметра - объект события
             */
            this.$emit('focus', this.$el);
        },
        async textareaBlurHandler() {
            this.focused = false;
            this.dirty = true;

            /**
             * Событие `blur` срабатывает при потере фокуса текстовым полем.
             * В качестве параметра - объект события
             */
            this.$emit('blur', this.$el);

            if (!this.noValidate) {
                await this.validate(this.modelValue);
            }
        },
        async textareaInputHandler() {
            if (this.dirty && !this.noValidate) {
                await this.$nextTick();
                await this.validate(this.modelValue);
            }

            this.setTextareaHeight();

            const { offsetHeight } = this.$refs.textarea;

            this.textareaSizes.offsetHeight = offsetHeight;
        },
        textareaScrollHandler() {
            /**
             * Эмит события `scroll`, срабатывает при скролле внутри текстового поля
             */
            this.$emit('scroll');
            this.calcScroll();
        },
        async setTextareaHeight(isAutoGrow = this.autoGrow) {
            if (this.resizable) return;

            this.textareaSizes.height = null;

            if (!isAutoGrow) return;

            await this.$nextTick();

            if (!this.$refs.textarea) return;

            const {
                offsetHeight,
                scrollHeight,
            } = this.$refs.textarea;

            this.textareaSizes.height = scrollHeight > offsetHeight
                ? `${scrollHeight}px`
                : `${offsetHeight}px`;

            if (this.maxHeight) {
                this.isScrollVisible = scrollHeight > this.textareaSizes.offsetHeight;
            }
        },
        async calcScroll() {
            await this.$nextTick();
            this.calcIsScrollVisible();
            setTimeout(this.calcIsScrollTop);
            setTimeout(this.calcIsScrollBottom);
        },
        calcIsScrollVisible() {
            if (!this.$refs.textarea || this.autoGrow) return;

            const {
                offsetHeight,
                scrollHeight,
            } = this.$refs.textarea;

            if (!this.isAutoGrow) {
                this.isScrollVisible = scrollHeight > offsetHeight;
            }
        },
        calcIsScrollTop() {
            if (!this.$refs.textarea) return;

            const { scrollTop } = this.$refs.textarea;
            const initialIsScrollTop = this.isScrollTop; // чтобы не эмитить событие повторно

            this.isScrollTop = scrollTop === 0;

            if (
                this.isScrollVisible &&
                this.isScrollTop &&
                this.isScrollTop !== initialIsScrollTop) {
                /**
                 * Эмит события `scroll:reachStart`, срабатывает при скролле до начала текстового поля
                 */
                this.$emit('scroll:reachStart');
            }
        },
        calcIsScrollBottom() {
            if (!this.$refs.textarea) return;

            const {
                offsetHeight,
                scrollHeight,
                scrollTop,
            } = this.$refs.textarea;
            const initialIsScrollBottom = this.isScrollBottom; // чтобы не эмитить событие повторно

            this.isScrollBottom = scrollHeight <= offsetHeight + scrollTop;

            if (
                this.isScrollVisible &&
                this.isScrollBottom &&
                this.isScrollBottom !== initialIsScrollBottom) {
                /**
                 * Эмит события `scroll:reachEnd`, срабатывает при скролле до конца текстового поля
                 */
                this.$emit('scroll:reachEnd');
            }
        },
        setTextareaCssVarTheme(colorTheme) {
            if (!colorTheme) return;

            this.$el.style.setProperty(
                '--textarea-color-theme',
                `var(--${colorTheme})`,
            );
        },
        async update() {
            await this.$nextTick();
            await this.setTextareaHeight();
            this.calcScroll();
        },
    },
    inheritAttrs: false,
    props: {
        /**
         * Определяет будет ли текстовое поле увеличивать высоту, когда текст превышает размер.
         * Игнорируется, если включен параметр `resizable`
         */
        autoGrow: {
            type: Boolean,
            default: false,
        },
        /**
         * Определяет наличие бордера
         */
        // eslint-disable-next-line vue/no-unused-properties
        bordered: {
            type: Boolean,
            default: true,
        },
        /**
         * Отображение счётчика введеных символов
         */
        counter: {
            type: Boolean,
            default: true,
        },
        /**
         * Перевод текстового поля в неактивное состояние
         */
        disabled: {
            type: Boolean,
            default: false,
        },
        /**
         * Сообщение при ошибке
         */
        errorMessage: {
            type: String,
            default: '',
        },
        /**
         * Определяет будет ли текстовое поле занимать всю доступную ширину.
         * Игнорируется, если включен параметр `resizable`
         */
        fullWidth: {
            type: Boolean,
            default: false,
        },
        /**
         * Тема (цветовые решения) для цвета бордера при наведении
         */
        // eslint-disable-next-line vue/no-unused-properties
        hoverBorder: {
            type: String,
            default: AllColors.CURRENT_COLOR,
        },
        /**
         * Уникальный идентификатор текстового поля
         */
        id: {
            type: String,
            default: '',
        },
        /**
         * Лейбл для текстового поля
         */
        label: {
            type: String,
            default: '',
        },
        /**
         * Максимально разрешенное количество символов
         */
        maxLength: {
            type: [Number, String],
            default: null,
            validator: isValueConvertedToNumber,
        },
        /**
         * Минимально необходимое количество символов
         */
        minLength: {
            type: [Number, String],
            default: null,
            validator: isValueConvertedToNumber,
        },
        /**
         * Определяет возможность изменения размера текстового поля
         */
        resizable: {
            type: Boolean,
            default: false,
        },
        /**
         * Определяет количество строк текстового поля
         */
        rows: {
            type: [Number, String],
            default: 4,
            validator: isValueConvertedToNumber,
        },
        /**
         * Класс для текстового поля
         */
        textareaFieldClass: {
            type: String,
            default: '',
        },
        /**
         * Класс для лейбла
         */
        textareaLabelClass: {
            type: String,
            default: '',
        },
        /**
         * Класс для обертки текстового поля
         */
        textareaWrapperClass: {
            type: String,
            default: '',
        },
        /**
         * Значение, введенное в текстовое поле
         * @model
         */
        modelValue: {
            type: String,
            default: '',
        },
    },
    watch: {
        colorTheme(val) {
            this.setTextareaCssVarTheme(val);
        },
        autoGrow(val) {
            this.setTextareaHeight(val);

            if (!val) this.calcScroll();
        },
        fullWidth() {
            this.setTextareaHeight();
            this.calcScroll();
        },
        resizable(val) {
            if (!val) {
                this.setTextareaHeight();
                this.calcScroll();
            }
        },
    },
    created() {
        this.resizeObserver = new ResizeObserver(this.update);
        this.textareaScrollHandler = _throttle(
            this.textareaScrollHandler,
            SCROLL_THROTTLE_MS,
        );
    },
    async mounted() {
        if (this.$refs.textarea) {
            this.resizeObserver.observe(this.$refs.textarea);
        }

        await this.update();
        this.setTextareaCssVarTheme(this.colorTheme);
    },
    beforeUnmount() {
        this.resizeObserver.disconnect();
    },
};
</script>

<style lang="scss">
$textarea-padding-x: 20px;
$textarea-padding-y: 15px;

.sbs-textarea {
    --textarea-color-theme: #{$success};

    position: relative;
    display: inline-flex;
    flex-direction: column;
    max-width: 100%;

    &.full-width {
        width: 100%;
    }

    &__wrapper {
        position: relative;
        display: flex;
        flex-grow: 1;
        color: $success;
        background-color: $white;
        border-color: $gray;
        border-radius: $rounded-10;
        cursor: text;

        @include typo-secondary-book;

        .sbs-textarea.disabled & {
            background-color: $gray-ultra-light;
            border-color: $gray-light;
        }

        .sbs-textarea:not(.disabled).focused & {
            border-color: $current-color;
        }

        .sbs-textarea:not(.disabled).invalid & {
            border-color: $danger;
        }
    }

    &__field-wrapper {
        position: relative;
        display: flex;
        width: 100%;
        overflow: hidden;
        border-radius: $rounded-10;

        &::before,
        &::after {
            position: absolute;
            right: var(--scrollbar-width);
            left: 0;
            height: 25px;
            transition: $trs;
            content: "";
            pointer-events: none;
        }

        &::before {
            position: absolute;
            top: 0;
            right: 0;
            left: 0;
            background-image:
                linear-gradient(
                    to top,
                    rgba($white, 0) 0,
                    $white 100%
                );
            border-top-left-radius: $rounded-10;
            border-top-right-radius: $rounded-10;

            .sbs-textarea.scroll-top & {
                opacity: 0;
            }
        }

        &::after {
            position: absolute;
            right: 0;
            bottom: 0;
            left: 0;
            background-image:
                linear-gradient(
                    to bottom,
                    rgba($white, 0) 0,
                    $white 100%
                );
            border-bottom-right-radius: $rounded-10;
            border-bottom-left-radius: $rounded-10;

            .sbs-textarea.scroll-bottom & {
                opacity: 0;
            }
        }
    }

    &__field {
        width: 100%;
        padding-top: $textarea-padding-y;
        padding-right: $textarea-padding-x;
        padding-bottom: $textarea-padding-y;
        padding-left: $textarea-padding-x;
        overflow: hidden;
        font: inherit;
        background-color: $transparent;
        border: none;
        outline: none;
        transition-property: opacity;
        resize: none;

        @include scrollbar(
            var(--scrollbar-width),
            $gray,
            var(--textarea-color-theme),
            var(--textarea-color-theme),
            $transparent,
        );

        .sbs-textarea.scroll-visible:not(.resizable) & {
            overflow-y: scroll;
        }

        .sbs-textarea.scroll-visible & {
            padding-right: calc(#{$textarea-padding-x} - var(--scrollbar-width));
        }

        .sbs-textarea.resizable & {
            resize: both;
        }

        .sbs-textarea.disabled & {
            color: rgba($black, 0.4);
        }

        &::placeholder {
            color: $gray-deep;
        }
    }

    &__counter {
        position: absolute;
        right: $textarea-padding-x;
        bottom: 8px;
        z-index: 1;
        max-width: calc(100% - #{$textarea-padding-x * 2});
        padding: 0 5px;
        color: $gray-deep;
        text-align: right;
        background-color: $white;
        border-radius: $rounded-10;
        cursor: default;
        opacity: 0;
        user-select: none;
        pointer-events: none;

        @include typo-subsecondary-demi;

        .sbs-textarea.focused & {
            opacity: 1;
            pointer-events: all;

            @include has-hover {
                &:hover {
                    opacity: 0.2;
                }
            }
        }
    }

    &__label {
        position: absolute;
        top: $textarea-padding-y;
        right: $textarea-padding-x - 5px;
        left: $textarea-padding-x - 5px;
        z-index: 10;
        display: flex;
        color: rgba($gray-deep, 0.4);
        cursor: text;
        transition-timing-function: linear;

        .sbs-textarea:not(.empty) &,
        .sbs-textarea.focused & {
            transform: translateY(-23px);
        }

        .sbs-textarea:not(.disabled):not(.invalid).focused & {
            color: $current-color;
        }

        &--top {
            @include typo-subsecondary-demi;
        }
    }

    &__label-text {
        position: relative;
        padding-right: 5px;
        padding-left: 5px;
        border-radius: $rounded-10;

        &::before {
            position: absolute;
            top: 0;
            left: 0;
            z-index: -1;
            width: 100%;
            height: 100%;
            background-image:
                linear-gradient(
                    to bottom,
                    $white 0,
                    $white 50%,
                    $transparent 75%,
                );
            opacity: 0;
            transition: opacity $trs;
            content: "";

            .sbs-textarea.disabled.scroll-top & {
                background-image:
                    linear-gradient(
                        to bottom,
                        $white 0,
                        $white 40%,
                        $gray-ultra-light 50%,
                        $transparent 60%,
                    );
            }

            .sbs-textarea.label-top & {
                opacity: 1;
            }
        }

        .sbs-textarea.label-top & {
            @include typo-subsecondary-demi;
        }
    }

    &__error-message {
        margin-top: 8px;
        color: $danger;

        @include typo-subsecondary-demi;
    }

    &__error-icon {
        width: 15px;
        height: 15px;
        margin-right: 5px;
        border-radius: $rounded-circle;
    }
}
</style>
